import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { ToolbarActions } from "@/client/components/page";
import { HeldControl } from "@/client/components/page-pending";
import { PageFailed } from "@/client/components/route-states";
import { TableShapeSkeleton } from "@/client/components/table-skeleton";
import { pageTitle } from "@/client/lib/page-title";
import { PENDING_MS } from "@/client/lib/pending";
import { holidaysQuery, scheduleQuery } from "@/client/lib/schedule-query";
import { ensureTenantData, requireModule } from "@/client/lib/session";
import { useNow } from "@/client/lib/use-now";
import {
  monthLeaveQuery,
  monthQuery,
} from "@/features/attendance/attendance-query";
import { TeamMonth } from "@/features/attendance/components/team-month";
import { attendanceCopy } from "@/features/attendance/copy";
import { changesHoursOf, setsOthersHours } from "@/features/attendance/roles";
import { monthIn } from "@/features/attendance/rules";
import { teamMonthSearch } from "@/features/attendance/schemas";
import { peopleQuery } from "@/features/people/people-query";

/** An open day's hours run to now, so the totals re-read the clock twice a minute. */
const TICK = 30_000;

export const Route = createFileRoute("/_authenticated/$slug/attendance/month")({
  pendingMs: PENDING_MS,
  // A missing month is the current one in the company's timezone, never the browser's, so it is
  // filled here rather than by the schema.
  validateSearch: teamMonthSearch,
  loaderDeps: ({ search }) => ({ month: search.month }),
  beforeLoad: ({ context, params }) => {
    requireModule(context, "attendance");
    // The team's month is a Manager's or an Admin's; an Employee's Attendance is their own.
    if (!setsOthersHours(context.role)) {
      throw redirect({ to: "/$slug/attendance/my-month", params });
    }
  },
  loader: async ({ context, deps }) => {
    const month = deps.month ?? monthIn(context.companyTimezone, new Date());
    await Promise.all([
      ensureTenantData(context.queryClient, monthQuery(month)),
      ensureTenantData(context.queryClient, scheduleQuery),
      ensureTenantData(
        context.queryClient,
        monthLeaveQuery(month, context.companyModules.includes("leave")),
      ),
      ensureTenantData(
        context.queryClient,
        holidaysQuery(Number(month.slice(0, 4))),
      ),
      ensureTenantData(context.queryClient, peopleQuery),
    ]);
  },
  head: ({ match }) => ({
    meta: [
      { title: pageTitle(attendanceCopy.title, match.context.companyName) },
    ],
  }),
  pendingComponent: MonthPending,
  errorComponent: PageFailed,
  component: Month,
});

/** A month's first load: the download waits for the month; the frame draws the stepper. */
function MonthPending() {
  return (
    <>
      <ToolbarActions>
        <HeldControl className="size-[var(--size-touch)] @panel-md/main:w-40" />
      </ToolbarActions>
      <TableShapeSkeleton />
    </>
  );
}

function Month() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const { companyTimezone, companyModules, email, role } =
    Route.useRouteContext();
  const now = useNow(TICK);
  const month = search.month ?? monthIn(companyTimezone, now);
  const leaveOn = companyModules.includes("leave");

  const { data: rows } = useSuspenseQuery(monthQuery(month));
  const { data: schedule } = useSuspenseQuery(scheduleQuery);
  const { data: leave } = useSuspenseQuery(monthLeaveQuery(month, leaveOn));
  const { data: holidays } = useSuspenseQuery(
    holidaysQuery(Number(month.slice(0, 4))),
  );
  // The session carries an email, not a person id, so the viewer is found by email, compared
  // case-insensitively as the invitation lookup and account link do.
  const { data: people } = useSuspenseQuery(peopleQuery);
  const asked = email?.toLowerCase();
  const members = people.filter((row) => row.kind === "person");
  const viewerPersonId =
    members.find((row) => row.email.toLowerCase() === asked)?.id ?? null;

  return (
    <TeamMonth
      rows={rows}
      context={{
        month,
        schedule,
        holidays,
        leave,
        timeZone: companyTimezone,
        now,
      }}
      people={members}
      viewerPersonId={viewerPersonId}
      mayChange={(personId) =>
        changesHoursOf({ personId: viewerPersonId, role }, personId, members)
      }
      showsLeave={leaveOn}
      search={{ ...search, month }}
      // Replaced so Back leaves Attendance instead of stepping through every month and panel.
      onSearch={(change) =>
        navigate({ search: { ...search, ...change }, replace: true })
      }
    />
  );
}
